<template>
  <div class="page-container reveal">
    <!-- Table View -->
    <el-card v-if="!selected" class="main-list-card">
      <div class="list-header">
        <div class="header-content">
          <h2 class="page-title">Répertoire des Artisans</h2>
          <p class="page-subtitle">{{ filtered.length }} experts référencés sur la plateforme</p>
        </div>
        <div class="header-actions">
          <el-input
            v-model="searchQ"
            placeholder="Nom, téléphone, CNIB..."
            class="search-input"
            @input="onSearch"
          >
            <template #prefix><el-icon><Search /></el-icon></template>
          </el-input>
          
          <el-select v-model="approvalFilter" @change="onSearch" class="filter-select">
            <el-option label="Tous" value="" />
            <el-option label="Approuvés" value="approved" />
            <el-option label="En attente" value="pending" />
            <el-option label="Brouillon" value="draft" />
            <el-option label="En révision" value="needs_revision" />
            <el-option label="Suspendus" value="suspended" />
            <el-option label="Rejetés" value="rejected" />
          </el-select>

          <el-button :icon="Download" @click="handleExportCsv" title="Exporter les artisans en fichier Excel/CSV">
            Exporter CSV
          </el-button>
          <el-button @click="load" :loading="isLoading" circle><el-icon><Refresh /></el-icon></el-button>
        </div>
      </div>

      <div v-if="selectedRows.length && (canReviewArtisans || canSanctionArtisans)" class="bulk-bar">
        <span class="bulk-count">{{ selectedRows.length }} sélectionné(s)</span>
        <el-button v-if="canReviewArtisans" type="success" size="small" :loading="bulkLoading" @click="bulkApprove" :icon="Check">Approuver</el-button>
        <el-button v-if="canReviewArtisans" type="danger" size="small" :loading="bulkLoading" @click="bulkReject" :icon="Close">Rejeter</el-button>
        <el-button v-if="canSanctionArtisans" type="warning" size="small" :loading="bulkLoading" @click="bulkSuspend" :icon="Warning">Suspendre</el-button>
      </div>

      <el-table :data="paged" v-loading="isLoading" class="custom-table" @selection-change="onSelectionChange">
        <el-table-column type="selection" width="45" />
        <el-table-column label="Identité" min-width="240" sortable :sort-method="(a, b) => artisanName(a).localeCompare(artisanName(b))">
          <template #default="{ row }">
            <div class="artisan-identity">
              <div class="avatar-wrapper">
                <AuthFileImg v-if="row.photo_url" :path="row.photo_url" class="artisan-avatar" />
                <div v-else class="avatar-placeholder">{{ artisanInitial(row) }}</div>
              </div>
              <div class="identity-info">
                <div class="artisan-name">{{ artisanName(row) }}</div>
                <div class="artisan-phone">{{ formatPhone(row.whatsapp || row.phone) }}</div>
              </div>
            </div>
          </template>
        </el-table-column>
        
        <el-table-column label="Localisation" prop="quartier" sortable>
          <template #default="{ row }">
            <div class="location-tag"><el-icon><Location /></el-icon> {{ row.quartier || '—' }}</div>
          </template>
        </el-table-column>

        <el-table-column label="Vérifications" width="220">
          <template #default="{ row }">
            <div class="verif-badges">
              <el-tooltip content="Téléphone vérifié">
                <div class="verif-dot" :class="{ active: row.phone_verified }">
                  <el-icon><Iphone /></el-icon>
                </div>
              </el-tooltip>
              <el-tooltip content="CNIB / OCR">
                <div class="verif-dot" :class="{ active: row.cnib_ocr_verified, error: row.cnib_ocr_verified === false }">
                  <el-icon><Postcard /></el-icon>
                </div>
              </el-tooltip>
              <el-tooltip content="Note moyenne">
                <div class="rating-badge">
                  <el-icon><StarFilled /></el-icon> {{ row.rating_avg?.toFixed(1) || '—' }}
                </div>
              </el-tooltip>
            </div>
          </template>
        </el-table-column>

        <el-table-column label="Statut" width="160" sortable :sort-method="(a, b) => (a.approval_status || '').localeCompare(b.approval_status || '')">
          <template #default="{ row }">
            <div class="status-pill" :class="row.approval_status">
              {{ approvalLabel(row.approval_status) }}
            </div>
          </template>
        </el-table-column>

        <el-table-column label="Gestion" width="110" fixed="right" align="center">
          <template #default="{ row }">
            <el-button type="primary" size="small" @click="select(row)" class="btn-manage">Gérer</el-button>
          </template>
        </el-table-column>
      </el-table>

      <div class="pagination-area">
        <el-pagination
          v-if="filtered.length > pageSize"
          v-model:current-page="page"
          v-model:page-size="pageSize"
          layout="total, prev, pager, next"
          :total="filtered.length"
          @size-change="page = 1"
        />
      </div>
    </el-card>

    <!-- Detail View -->
    <el-card v-else class="detail-card reveal">
      <div class="detail-header">
        <el-button @click="closeDetail" :icon="ArrowLeft" class="btn-back">Retour</el-button>
        <h2 class="detail-title">Dossier : {{ artisanName(selected) }}</h2>
        <div class="status-pill" :class="selected.approval_status">
          {{ approvalLabel(selected.approval_status) }}
        </div>
      </div>

      <div class="detail-body">
        <div class="detail-grid">
          <div class="info-group">
            <h4 class="group-title">Coordonnées</h4>
            <div class="grid-2">
              <div class="detail-row"><span class="lbl">Tél</span><span class="val">{{ formatPhone(selected.phone) }}</span></div>
              <div class="detail-row"><span class="lbl">WhatsApp</span><span class="val">{{ formatPhone(selected.whatsapp) }}</span></div>
              <div class="detail-row"><span class="lbl">Ville/Quartier</span><span class="val">{{ selected.quartier || '—' }}</span></div>
              <div class="detail-row">
                <span class="lbl">Tél vérifié</span>
                <span class="val">
                  <span :class="selected.phone_verified ? 'badge-ok' : 'badge-warn'">{{ selected.phone_verified ? 'Oui' : 'Non' }}</span>
                </span>
              </div>
            </div>
          </div>

          <div class="info-group">
            <h4 class="group-title">Compétences</h4>
            <div class="grid-2">
              <div class="detail-row"><span class="lbl">Spécialité</span><span class="val">{{ categoryName(selected.category_id) }}</span></div>
              <div class="detail-row"><span class="lbl">Expérience</span><span class="val">{{ selected.experience_years }} an(s)</span></div>
              <div class="detail-row"><span class="lbl">Motorisé</span><span class="val">{{ selected.has_motorcycle ? 'Oui' : 'Non' }}</span></div>
              <div class="detail-row"><span class="lbl">Diagnostic</span><span class="val">{{ selected.diagnostic_price?.toLocaleString() }} FCFA</span></div>
              <div class="detail-row"><span class="lbl">Inscription</span><span class="val">{{ fmtDate(selected.created_at) }}</span></div>
            </div>
          </div>

          <div class="info-group">
            <h4 class="group-title">CNIB</h4>
            <div class="grid-2">
              <div class="detail-row"><span class="lbl">Numéro CNIB</span><span class="val">{{ selected.cnib_number || '—' }}</span></div>
              <div class="detail-row"><span class="lbl">NIP</span><span class="val">{{ selected.cnib_nip || '—' }}</span></div>
              <div class="detail-row"><span class="lbl">Délivré le</span><span class="val">{{ selected.cnib_issued_at || '—' }}</span></div>
              <div class="detail-row"><span class="lbl">Expire le</span><span class="val">{{ selected.cnib_expires_at || '—' }}</span></div>
              <div class="detail-row">
                <span class="lbl">Statut CNIB</span>
                <span class="val">
                  <span :class="selected.cnib_is_expired ? 'badge-error' : 'badge-ok'">{{ selected.cnib_is_expired ? 'Expirée' : 'Valide' }}</span>
                </span>
              </div>
              <div class="detail-row">
                <span class="lbl">OCR vérifié</span>
                <span class="val">
                  <span :class="selected.cnib_ocr_verified ? 'badge-ok' : 'badge-warn'">{{ selected.cnib_ocr_verified ? 'Oui' : 'Non' }}</span>
                </span>
              </div>
            </div>
          </div>

          <div class="info-group">
            <h4 class="group-title">Témoin / Personne de confiance</h4>
            <div class="grid-2">
              <div class="detail-row"><span class="lbl">Nom</span><span class="val">{{ selected.witness_last_name || '—' }}</span></div>
              <div class="detail-row"><span class="lbl">Prénom</span><span class="val">{{ selected.witness_first_name || '—' }}</span></div>
              <div class="detail-row"><span class="lbl">Téléphone</span><span class="val">{{ formatPhone(selected.witness_phone) }}</span></div>
            </div>
          </div>

          <div class="info-group span-2">
            <h4 class="group-title">Bio & Description</h4>
            <div class="bio-box">{{ selected.bio || 'Aucune biographie fournie.' }}</div>
          </div>

          <div v-if="hasDocuments" class="info-group span-2">
            <h4 class="group-title">Justificatifs & Documents</h4>
            <div class="doc-gallery">
              <div v-for="(url, key) in resolvedDocs" :key="key" class="doc-item">
                <template v-if="isPdf(url)">
                  <a :href="url" target="_blank" rel="noopener" class="doc-pdf-link">
                    <div class="doc-pdf-placeholder">
                      <el-icon :size="32"><Document /></el-icon>
                      <span>PDF</span>
                    </div>
                  </a>
                </template>
                <el-image v-else crossorigin="anonymous" :src="url" :preview-src-list="imageDocUrls" :initial-index="imageDocUrls.indexOf(url)" fit="cover" class="doc-img" :preview-teleported="true" hide-on-click-modal />
                <span class="doc-label">{{ documentLabels[key] }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="detail-footer">
        <div class="footer-actions-left">
          <template v-if="isPendingOrRevision(selected) && canReviewArtisans">
            <el-button type="success" :loading="actionLoading === selected.id" @click="approve(selected.id)" :icon="Check">Approuver</el-button>
            <el-button type="danger" :loading="actionLoading === selected.id" @click="confirmReject(selected.id)" :icon="Close">Rejeter</el-button>
            <el-button @click="showModifModal = true" :icon="Edit">Correction</el-button>
          </template>
          <el-button v-if="selected?.approval_status === 'approved' && canSanctionArtisans" type="warning" @click="onSanction(selected)" :icon="Warning">Sanctionner</el-button>
        </div>
        <div class="footer-actions-right">
          <el-button v-if="!isPendingOrRevision(selected)" @click="onViewChat(selected)" :icon="ChatDotRound">Historique Conversations</el-button>
        </div>
      </div>
    </el-card>

    <el-dialog append-to-body v-model="showModifModal" title="Demande de modification" width="480px">
      <el-input
        v-model="modifNotes"
        type="textarea"
        :rows="4"
        placeholder="Expliquer ce qui doit être corrigé..."
      />
      <template #footer>
        <el-button @click="showModifModal = false; modifNotes = ''">Annuler</el-button>
        <el-button type="primary" :disabled="!modifNotes.trim()" :loading="modifLoading" @click="sendModifRequest">Envoyer</el-button>
      </template>
    </el-dialog>

  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, defineEmits } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { Search, Refresh, Location, StarFilled, Iphone, Postcard, Check, Close, Edit, Warning, ChatDotRound, ArrowLeft, Document, Download } from '@element-plus/icons-vue'
import { approveArtisan, rejectArtisan, searchArtisans, requestArtisanModification, listCategories, setArtisanSanction } from '../services/adminApi'
import { extractApiError, getAuthFileUrl } from '../api'
import { useAdminPermissions } from '../composables/useAdminPermissions'
import AuthFileImg from './shared/AuthFileImg.vue'
import { formatPhone } from '../utils/format'
import { exportToCsv } from '../utils/exportCsv'

const emit = defineEmits(['sanction', 'view-chat'])

const handleExportCsv = () => {
  const approvalMap = {
    pending: 'En attente',
    draft: 'Brouillon',
    needs_revision: 'En révision',
    approved: 'Approuvé',
    suspended: 'Suspendu',
    rejected: 'Rejeté',
  }
  const columns = [
    { key: 'id', label: 'ID Artisan' },
    { key: 'full_name', label: 'Nom complet', format: (v) => v || '—' },
    { key: 'phone', label: 'Téléphone', format: (v) => formatPhone(v) },
    { key: 'whatsapp', label: 'WhatsApp', format: (v) => formatPhone(v) },
    { key: 'approval_status', label: 'Statut Dossier', format: (v) => approvalMap[v] || v },
    { key: 'category_id', label: 'Catégorie Métier', format: (v) => categoryName(v) },
    { key: 'quartier', label: 'Zone / Quartier' },
    { key: 'rating_avg', label: 'Note Moyenne', format: (v) => v != null ? v.toFixed(1) : '—' },
    { key: 'rating_count', label: 'Nombre Avis', format: (v) => v || 0 },
    { key: 'is_online', label: 'En Ligne', format: (v) => v ? 'Oui' : 'Non' },
    { key: 'has_motorcycle', label: 'Motorisé', format: (v) => v ? 'Oui' : 'Non' },
    { key: 'created_at', label: 'Date Inscription', format: (v) => v ? new Date(v).toLocaleDateString('fr-FR') : '' },
  ]
  const dateStr = new Date().toISOString().slice(0, 10)
  exportToCsv(`artisans_profissa_${dateStr}.csv`, columns, artisans.value)
}

const selectedRows  = ref([])
const artisans      = ref([])
const isLoading     = ref(false)
const actionLoading = ref(null)
const selected      = ref(null)
const showDetail    = ref(false)
const searchQ       = ref('')
const approvalFilter= ref('')
const page          = ref(1)
const pageSize      = ref(15)
const showModifModal= ref(false)
const modifNotes    = ref('')
const modifLoading  = ref(false)
const categories    = ref([])
const { hasPermission } = useAdminPermissions()
const canReviewArtisans = computed(() => hasPermission('artisans.review.any'))
const canSanctionArtisans = computed(() => hasPermission('artisans.sanction.any'))

const categoryName = (id) => {
  if (!id) return '—'
  const cat = categories.value.find(c => c.id === id)
  return cat ? (cat.label_fr || cat.name) : id
}

const documentLabels = {
  photo_url: 'Photo Profil',
  cnib_front_url: 'CNIB Recto',
  cnib_back_url: 'CNIB Verso',
  selfie_url: 'Selfie',
  casier_url: 'Casier Judiciaire'
}

const artisanName    = (a) => a.full_name || a.phone || `Artisan #${a.id}`
const artisanInitial = (a) => artisanName(a).charAt(0).toUpperCase()
const fmtDate        = (d) => d ? new Date(d).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }) : '—'

const approvalLabel = (s) => ({
  draft: 'Brouillon', pending: 'En attente', needs_revision: 'À réviser',
  approved: 'Validé', rejected: 'Rejeté', suspended: 'Suspendu',
}[s] || s)

const filtered = computed(() => artisans.value)
const paged = computed(() => filtered.value.slice((page.value - 1) * pageSize.value, page.value * pageSize.value))

const resolvedDocs = ref({})
watch(selected, async (art) => {
  if (!art) { resolvedDocs.value = {}; return }
  const keys = ['photo_url', 'cnib_front_url', 'cnib_back_url', 'selfie_url', 'casier_url']
  const result = {}
  await Promise.all(keys.map(async (k) => {
    if (art[k]) result[k] = await getAuthFileUrl(art[k])
  }))
  resolvedDocs.value = result
}, { immediate: true })

const isPdf = (url) => /\.pdf(\?|$)/i.test(url)
const imageDocUrls = computed(() => Object.values(resolvedDocs.value).filter(u => !isPdf(u)))
const hasDocuments = computed(() => Object.keys(resolvedDocs.value).length > 0)
const isPendingOrRevision = (row) => row.approval_status === 'pending' || row.approval_status === 'needs_revision'

const load = async () => {
  isLoading.value = true
  try {
    artisans.value = await searchArtisans(searchQ.value, approvalFilter.value)
    page.value = 1
  } catch (e) { ElMessage.error(extractApiError(e)) }
  finally { isLoading.value = false }
}

const onSelectionChange = (rows) => { selectedRows.value = rows }
const bulkLoading = ref(false)

const runBulk = async (label, action, candidates) => {
  if (!candidates.length) {
    ElMessage.warning(`Aucun artisan éligible pour "${label}".`)
    return
  }
  try {
    await ElMessageBox.confirm(
      `Appliquer "${label}" à ${candidates.length} artisan(s) ?`,
      'Action groupée',
      { type: 'warning', confirmButtonText: 'Confirmer', cancelButtonText: 'Annuler' }
    )
  } catch { return }

  bulkLoading.value = true
  let ok = 0; const errors = []
  for (const row of candidates) {
    try { await action(row.id); ok++ }
    catch (e) { errors.push(artisanName(row)) }
  }
  bulkLoading.value = false

  if (errors.length === 0) {
    ElMessage.success(`${ok} artisan(s) : ${label} avec succès.`)
  } else {
    ElMessage({
      type: 'warning',
      message: `${ok} succès, ${errors.length} échec(s) : ${errors.slice(0, 3).join(', ')}${errors.length > 3 ? '…' : ''}`,
      duration: 6000,
    })
  }
  selectedRows.value = []
  await load()
}

const bulkApprove = () => runBulk(
  'Approuver',
  approveArtisan,
  selectedRows.value.filter(r => ['pending', 'needs_revision'].includes(r.approval_status))
)
const bulkReject = () => runBulk(
  'Rejeter',
  rejectArtisan,
  selectedRows.value.filter(r => r.approval_status !== 'rejected')
)
const bulkSuspend = () => runBulk(
  'Suspendre',
  (id) => setArtisanSanction(id, { state: 'suspended', reason: 'Suspension administrative groupée' }),
  selectedRows.value.filter(r => r.approval_status !== 'suspended')
)

const onSearch = () => load()
const select = (a) => { selected.value = a; showDetail.value = true }
const closeDetail = () => { selected.value = null; showDetail.value = false }

const approve = async (id) => {
  if (!canReviewArtisans.value) return
  actionLoading.value = id
  try {
    await approveArtisan(id)
    ElMessage.success('Dossier validé avec succès.')
    await load(); closeDetail()
  } catch (e) { ElMessage.error(extractApiError(e)) }
  finally { actionLoading.value = null }
}

const confirmReject = async (id) => {
  if (!canReviewArtisans.value) return
  try {
    await ElMessageBox.confirm('Rejeter définitivement ce dossier ?', 'Attention', { type: 'warning' })
    actionLoading.value = id
    await rejectArtisan(id)
    ElMessage.success('Dossier rejeté.')
    await load(); closeDetail()
  } catch { /* cancelled */ }
  finally { actionLoading.value = null }
}

const sendModifRequest = async () => {
  if (!canReviewArtisans.value) return
  if (!selected.value || !modifNotes.value.trim()) return
  modifLoading.value = true
  try {
    await requestArtisanModification(selected.value.id, modifNotes.value.trim())
    ElMessage.success('Demande envoyée.')
    showModifModal.value = false; modifNotes.value = ''; await load()
  } catch (e) { ElMessage.error(extractApiError(e)) }
  finally { modifLoading.value = false }
}

const onSanction = (artisan) => {
  if (!canSanctionArtisans.value) return
  emit('sanction', artisan)
}
const onViewChat = (artisan) => emit('view-chat', artisan)

onMounted(async () => {
  await Promise.all([
    load(),
    listCategories().then(c => categories.value = c).catch(() => {}),
  ])
})
</script>

<style scoped>
.page-container { height: 100%; display: flex; flex-direction: column; }
.main-list-card { flex: 1; display: flex; flex-direction: column; }

.detail-card { flex: 1; display: flex; flex-direction: column; overflow: hidden; }
.detail-header { display: flex; align-items: center; gap: 1rem; padding-bottom: 1.5rem; border-bottom: 1px solid var(--glass-border); margin-bottom: 1.5rem; }
.btn-back { font-weight: 700; border-radius: 8px; }
.detail-title { font-size: 1.5rem; font-weight: 800; margin: 0; flex: 1; }
.detail-body { flex: 1; overflow-y: auto; padding-right: 10px; }
.detail-footer { display: flex; justify-content: space-between; align-items: center; padding-top: 1.5rem; border-top: 1px solid var(--glass-border); margin-top: 1.5rem; }

.list-header { display: flex; justify-content: space-between; align-items: flex-end; padding-bottom: 2rem; }
.page-title { font-size: 1.5rem; font-weight: 800; margin-bottom: 0.25rem; }
.page-subtitle { color: var(--text-muted); font-size: 0.9rem; font-weight: 600; }

.header-actions { display: flex; gap: 0.75rem; align-items: center; }
.search-input { width: 280px; }
.filter-select { width: 160px; }

/* Table Styling */
.artisan-identity { display: flex; align-items: center; gap: 1rem; }
.avatar-wrapper { width: 42px; height: 42px; border-radius: 12px; overflow: hidden; background: var(--surface-hover); border: 1px solid var(--glass-border); flex-shrink: 0; }
.artisan-avatar { width: 100%; height: 100%; object-fit: cover; }
.avatar-placeholder { width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; font-weight: 800; color: var(--accent); }
.artisan-name { font-weight: 700; font-size: 0.95rem; }
.artisan-phone { font-size: 0.8rem; color: var(--text-muted); }

.location-tag { font-size: 0.85rem; display: flex; align-items: center; gap: 0.4rem; color: var(--text-muted); }

.verif-badges { display: flex; gap: 0.75rem; align-items: center; }
.verif-dot { width: 28px; height: 28px; border-radius: 8px; background: var(--glass-bg); border: 1px solid var(--glass-border); display: flex; align-items: center; justify-content: center; color: var(--text-dim); }
.verif-dot.active { color: var(--success); background: rgba(34, 197, 94, 0.1); border-color: rgba(34, 197, 94, 0.2); }
.verif-dot.error { color: var(--error); background: rgba(239, 68, 68, 0.1); border-color: rgba(239, 68, 68, 0.2); }
.rating-badge { background: rgba(255, 184, 0, 0.1); color: var(--gold); padding: 4px 10px; border-radius: 8px; font-weight: 800; font-size: 0.8rem; display: flex; align-items: center; gap: 4px; }

.status-pill { font-size: 0.75rem; font-weight: 800; text-transform: uppercase; letter-spacing: 0.5px; padding: 4px 12px; border-radius: 20px; display: inline-block; }
.status-pill.draft { background: rgba(148, 163, 184, 0.1); color: #94a3b8; border: 1px solid rgba(148, 163, 184, 0.2); }
.status-pill.pending { background: rgba(245, 158, 11, 0.1); color: var(--warning); border: 1px solid rgba(245, 158, 11, 0.2); }
.status-pill.approved { background: rgba(34, 197, 94, 0.1); color: var(--success); border: 1px solid rgba(34, 197, 94, 0.2); }
.status-pill.rejected { background: rgba(239, 68, 68, 0.1); color: var(--error); border: 1px solid rgba(239, 68, 68, 0.2); }

.btn-manage { font-weight: 700; font-size: 0.85rem; }

.bulk-bar { display: flex; align-items: center; gap: 0.75rem; padding: 0.75rem 1rem; margin-bottom: 1rem; border-radius: 12px; background: rgba(255, 107, 0, 0.06); border: 1px solid rgba(255, 107, 0, 0.15); }
.bulk-count { font-weight: 800; font-size: 0.85rem; color: var(--accent); margin-right: auto; }

.pagination-area { padding-top: 1.5rem; display: flex; justify-content: flex-end; }

/* Dialog Styling */
.detail-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 2rem; padding: 1rem 0; }
.info-group { display: flex; flex-direction: column; gap: 1rem; }
.info-group.span-2 { grid-column: span 2; }
.group-title { font-family: 'Outfit', sans-serif; font-size: 0.9rem; text-transform: uppercase; letter-spacing: 1px; color: var(--accent); font-weight: 800; margin-bottom: 0.5rem; }
.grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 1rem; }

.detail-row { display: flex; flex-direction: column; gap: 0.25rem; }
.detail-row .lbl { font-size: 0.7rem; color: var(--text-dim); font-weight: 700; text-transform: uppercase; }
.detail-row .val { font-size: 1rem; font-weight: 600; }

.bio-box { background: var(--glass-bg); border: 1px solid var(--glass-border); border-radius: 12px; padding: 1.25rem; line-height: 1.6; color: var(--text-muted); font-size: 0.95rem; }

.doc-gallery { display: grid; grid-template-columns: repeat(auto-fill, minmax(140px, 1fr)); gap: 1rem; }
.doc-item { display: flex; flex-direction: column; gap: 0.5rem; }
.doc-img { width: 100%; height: 100px; border-radius: 10px; border: 1px solid var(--glass-border); }
.doc-label { font-size: 0.7rem; font-weight: 700; color: var(--text-dim); text-align: center; }

.dialog-footer { width: 100%; display: flex; justify-content: space-between; align-items: center; }
.footer-actions-left { display: flex; gap: 0.75rem; }
.footer-actions-right { display: flex; gap: 0.75rem; align-items: center; }

.badge-ok { color: var(--success); font-weight: 700; }
.badge-warn { color: var(--warning); font-weight: 700; }
.badge-error { color: var(--error); font-weight: 700; }

.doc-item { cursor: pointer; }
.doc-pdf-link { text-decoration: none; display: block; }
.doc-pdf-placeholder { width: 100%; height: 100px; border-radius: 10px; border: 1px solid var(--glass-border); background: var(--glass-bg); display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 4px; color: var(--text-muted); transition: background 0.2s; }
.doc-pdf-placeholder:hover { background: var(--surface-hover); }
.doc-pdf-placeholder span { font-size: 0.75rem; font-weight: 700; }
</style>

<style>
/* Make el-image-viewer close button bigger and more visible */
.el-image-viewer__close { font-size: 28px !important; top: 20px !important; right: 20px !important; width: 48px !important; height: 48px !important; background: rgba(0,0,0,0.6) !important; border-radius: 50% !important; color: #fff !important; }
/* Close viewer when clicking the dark backdrop (mask) */
.el-image-viewer__mask { cursor: pointer; }
</style>
